import { MAX_LEVEL, expectedIlvl } from '../../content/balance';
import { NPCS } from '../../content/npcs';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import {
  type Cost,
  canAfford,
  freeSlots,
  itemValue,
  pay,
  sellItem,
  sellStack,
} from '../../game/inventory';
import {
  type GearSlot,
  type Rarity,
  GEAR_SLOTS,
  ITEMS,
  gearName,
  makeGear,
} from '../../game/items';
import type { Progression } from '../../game/Progression';
import { roomFor, scaleCost } from './bag';

/**
 * NPC vendors and token exchanges. Vendors sell consumables/gear for silver
 * and buy anything back (no buyback); exchanges trade activity tokens for
 * materials and set gear with weekly limits.
 */

export type ShopPeriod = 'day' | 'week' | 'ever';

export interface ShopGear {
  slot: GearSlot;
  ilvl: number;
  rarity: Rarity;
  set?: string;
  quality?: number;
}

export interface ShopEntry {
  id: string;
  /** Stackable catalog item… */
  item?: string;
  /** …received `count` at a time (default 1). */
  count?: number;
  /** …or a gear piece minted on purchase. */
  gear?: ShopGear;
  price: Cost;
  limit?: { n: number; per: ShopPeriod };
  minLevel?: number;
  /** Bounty Board reputation tier needed (seal exchange). */
  minRep?: number;
}

export interface ShopDef {
  id: string;
  name: string;
  blurb: string;
  /** Vendors buy items back (Sell tab); exchanges do not. */
  vendor: boolean;
  /** Tokens to show in the header next to silver/gold. */
  tokens?: string[];
  /** Exchanges in the same group are shown as sibling tabs. */
  group?: string;
  entries: (c: CharacterSave) => ShopEntry[];
}

export const SHOPS: Record<string, ShopDef> = {};

export function registerShop(def: ShopDef): void {
  SHOPS[def.id] = def;
}

/** The shop an NPC runs (panelArg may be an NPC id or a shop id). */
export function resolveShop(arg: string | null): ShopDef | null {
  if (!arg) return null;
  if (SHOPS[arg]) return SHOPS[arg];
  const shopId = NPCS[arg]?.shop;
  return shopId ? (SHOPS[shopId] ?? null) : null;
}

export function shopGroup(shop: ShopDef): ShopDef[] {
  if (!shop.group) return [shop];
  return Object.values(SHOPS).filter((s) => s.group === shop.group);
}

// ---------------------------------------------------------------- catalog helpers

const s = (item: string, silver: number, opts: Partial<ShopEntry> = {}): ShopEntry => ({
  id: item,
  item,
  price: { silver },
  ...opts,
});
const t = (
  item: string,
  count: number,
  token: string,
  n: number,
  opts: Partial<ShopEntry> = {},
): ShopEntry => ({
  id: `${item}_${count}`,
  item,
  count,
  price: { items: { [token]: n } },
  ...opts,
});
const week = (n: number) => ({ limit: { n, per: 'week' as const } });

/** Set armour costs `armor` tokens, the weapon `weapon` tokens (+ silver), all at one item level. */
function setGear(
  token: string,
  armor: number,
  weapon: number,
  silver: number,
  ilvl: number,
  rarity: Rarity,
  set: string,
): ShopEntry[] {
  return GEAR_SLOTS.map((slot) => {
    const w = slot === 'weapon';
    return {
      id: `${set}_${slot}`,
      gear: { slot, ilvl, rarity, set },
      price: { silver: w ? silver * 2 : silver, items: { [token]: w ? weapon : armor } },
      minLevel: MAX_LEVEL,
    };
  });
}

const OUTPOST: ShopEntry[] = [
  s('potion_small', 25),
  s('phoenix_plume', 150),
  s('campfire_kit', 60),
  s('grenade_flame', 90, { minLevel: 3 }),
  s('grenade_flash', 90, { minLevel: 5 }),
  s('potion_panacea', 80, { minLevel: 6 }),
];

const POTIONS: ShopEntry[] = [
  s('potion_small', 25),
  s('potion_major', 90, { minLevel: 10 }),
  s('potion_elixir', 260, { minLevel: 20 }),
  s('grenade_flame', 90),
  s('grenade_frost', 90),
  s('grenade_flash', 90),
  s('grenade_clay', 90),
  s('bomb_corrosive', 110, { minLevel: 12 }),
  s('bomb_sleep', 110, { minLevel: 12 }),
  s('grenade_whirl', 160, { minLevel: 15 }),
  s('grenade_dark', 160, { minLevel: 15 }),
  s('bomb_destruction', 180, { minLevel: 15 }),
  s('potion_panacea', 80),
  s('potion_protect', 120),
  s('flag_march', 100),
  s('potion_timestop', 300, { minLevel: 20 }),
  s('campfire_kit', 60),
  s('phoenix_plume', 150),
];

registerShop({
  id: 'outpost',
  name: 'Outpost Supplies',
  blurb: 'Field rations for new Shardbearers.',
  vendor: true,
  entries: () => OUTPOST,
});
registerShop({
  id: 'havenreach_potions',
  name: "Tessa's Tonics",
  blurb: 'Draughts, grenades and bombs for every fight.',
  vendor: true,
  entries: () => POTIONS,
});
registerShop({
  id: 'havenreach_gear',
  name: "Kael's Armoury",
  blurb: 'Serviceable gear cut to your level. Loot will outpace it; it will not leave you bare.',
  vendor: true,
  entries: (c) => {
    const ilvl = Math.min(245, expectedIlvl(c.level) + 4);
    const gear = GEAR_SLOTS.map((slot): ShopEntry => ({
      id: `gear_${slot}`,
      gear: { slot, ilvl, rarity: 'rare', quality: 40 },
      price: { silver: Math.round((180 + 60 * c.level) * (slot === 'weapon' ? 1.6 : 1)) },
    }));
    return [...gear, s('song_return', 500), s('phoenix_plume', 150)];
  },
});
registerShop({
  id: 'sunscar_caravan',
  name: 'Caravan Stores',
  blurb: 'Water, salves and anything that keeps the sand out.',
  vendor: true,
  entries: () => [
    s('potion_small', 28),
    s('potion_major', 95, { minLevel: 10 }),
    s('grenade_frost', 95),
    s('grenade_clay', 95),
    s('bomb_corrosive', 115),
    s('potion_panacea', 85),
    s('flag_march', 105),
    s('campfire_kit', 65),
    s('phoenix_plume', 160),
  ],
});
registerShop({
  id: 'frosthollow_abbey',
  name: 'Abbey Provisions',
  blurb: 'Warm hands fight better. So do full flasks.',
  vendor: true,
  entries: () => [
    s('potion_major', 95),
    s('potion_elixir', 270, { minLevel: 20 }),
    s('grenade_flame', 95),
    s('grenade_dark', 165),
    s('potion_protect', 125),
    s('potion_timestop', 310, { minLevel: 20 }),
    s('campfire_kit', 65),
    s('phoenix_plume', 160),
  ],
});

// Token exchanges (research 02 §11.5: weekly-limited token shops).
registerShop({
  id: 'rift_scholar',
  name: 'Rift Sigils',
  blurb: 'Elowen trades what she salvages from the rifts for the sigils you bring back.',
  vendor: false,
  group: 'exchange',
  tokens: ['rift_sigil'],
  entries: () => [
    t('leap_crystal', 5, 'rift_sigil', 12, week(10)),
    t('ore_weapon', 20, 'rift_sigil', 8, week(10)),
    t('ore_armor', 50, 'rift_sigil', 8, week(10)),
    t('catalyst', 1, 'rift_sigil', 15, week(5)),
    t('gem_dmg_1', 1, 'rift_sigil', 30, week(2)),
    t('gem_cd_1', 1, 'rift_sigil', 30, week(2)),
    t('potion_elixir', 3, 'rift_sigil', 10, week(3)),
    t('rift_energy_tonic', 1, 'rift_sigil', 60, week(1)),
  ],
});
registerShop({
  id: 'colossus_marks',
  name: 'Colossus Marks',
  blurb: 'Trophies of the great hunts, weighed and paid in forge stock.',
  vendor: false,
  group: 'exchange',
  tokens: ['colossus_mark'],
  entries: () => [
    t('leap_crystal', 10, 'colossus_mark', 14, week(10)),
    t('catalyst', 2, 'colossus_mark', 18, week(5)),
    t('grenade_whirl', 3, 'colossus_mark', 6, week(5)),
    t('bomb_destruction', 3, 'colossus_mark', 6, week(5)),
    t('gem_dmg_2', 1, 'colossus_mark', 60, week(1)),
    t('gem_cd_2', 1, 'colossus_mark', 60, week(1)),
    t('forge_tome', 1, 'colossus_mark', 90, week(1)),
  ],
});
registerShop({
  id: 'reliquary_seals',
  name: 'Reliquary Seals',
  blurb: 'Deepvault seals buy Reliquary set gear (item level 300). Armour 2 seals, weapon 5.',
  vendor: false,
  group: 'exchange',
  tokens: ['vault_token'],
  entries: () => setGear('vault_token', 2, 5, 3000, 300, 'legendary', 'reliquary'),
});
registerShop({
  id: 'warlord_relics',
  name: "Warlord's Relics",
  blurb:
    'Relics of the Hollow Throne forge the Hollowbane set (item level 340). Armour 4 relics, weapon 8.',
  vendor: false,
  group: 'exchange',
  tokens: ['warlord_relic'],
  entries: () => setGear('warlord_relic', 4, 8, 6000, 340, 'relic', 'warlord'),
});
registerShop({
  id: 'bounty_seals',
  name: 'Seal Exchange',
  blurb: 'Quill honours Bounty Seals. Higher standing opens the good crates.',
  vendor: false,
  tokens: ['bounty_seal'],
  entries: () => [
    t('potion_major', 5, 'bounty_seal', 3),
    t('phoenix_plume', 3, 'bounty_seal', 4),
    t('leap_crystal', 3, 'bounty_seal', 6, { minRep: 1 }),
    t('catalyst', 1, 'bounty_seal', 10, { minRep: 2, ...week(3) }),
    t('gem_dmg_1', 1, 'bounty_seal', 18, { minRep: 2, ...week(1) }),
    t('gem_cd_1', 1, 'bounty_seal', 18, { minRep: 2, ...week(1) }),
    t('rift_energy_tonic', 1, 'bounty_seal', 25, { minRep: 3, ...week(1) }),
    t('skill_potion', 1, 'bounty_seal', 60, { minRep: 3, limit: { n: 3, per: 'ever' } }),
    t('forge_tome', 1, 'bounty_seal', 80, { minRep: 4, ...week(1) }),
  ],
});

// ---------------------------------------------------------------- limits

const periodKey = (per: ShopPeriod, a: AccountSave): number =>
  per === 'day' ? a.day : per === 'week' ? a.week : 0;
const limitKey = (shop: ShopDef, e: ShopEntry) => `${shop.id}.${e.id}`;

/** Purchases of a limited entry in the current period. */
export function boughtThisPeriod(
  c: CharacterSave,
  a: AccountSave,
  shop: ShopDef,
  e: ShopEntry,
): number {
  if (!e.limit) return 0;
  const k = limitKey(shop, e);
  if ((c.counters[`shopp.${k}`] ?? -1) !== periodKey(e.limit.per, a)) return 0;
  return c.counters[`shopq.${k}`] ?? 0;
}

export function remainingFor(
  c: CharacterSave,
  a: AccountSave,
  shop: ShopDef,
  e: ShopEntry,
): number {
  return e.limit ? Math.max(0, e.limit.n - boughtThisPeriod(c, a, shop, e)) : Infinity;
}

function recordPurchase(
  c: CharacterSave,
  a: AccountSave,
  shop: ShopDef,
  e: ShopEntry,
  n: number,
): void {
  if (!e.limit) return;
  const k = limitKey(shop, e);
  const cur = boughtThisPeriod(c, a, shop, e);
  c.counters[`shopp.${k}`] = periodKey(e.limit.per, a);
  c.counters[`shopq.${k}`] = cur + n;
}

// ---------------------------------------------------------------- buying

export type ShopResult =
  | { ok: true; items: { id: string; count: number }[]; equip: { name: string; rarity: Rarity }[] }
  | { ok: false; reason: string };

export function entryName(e: ShopEntry): string {
  if (e.item) return ITEMS[e.item]?.name ?? e.item;
  if (e.gear) return gearName(e.gear.slot, e.gear.ilvl, e.gear.rarity, e.gear.set);
  return e.id;
}

export function entryRarity(e: ShopEntry): Rarity {
  if (e.item) return ITEMS[e.item]?.rarity ?? 'normal';
  return e.gear?.rarity ?? 'normal';
}

/** Why an entry can't be bought right now (null = purchasable), for `qty` purchases. */
export function blockReason(
  p: Progression,
  shop: ShopDef,
  e: ShopEntry,
  qty: number,
  rep = 0,
): string | null {
  const c = p.char;
  if (qty < 1) return 'Choose a quantity';
  if (e.minLevel && c.level < e.minLevel) return `Requires level ${e.minLevel}`;
  if (e.minRep && rep < e.minRep) return 'Requires a higher Bounty standing';
  if (qty > remainingFor(c, p.account, shop, e))
    return e.limit?.per === 'ever'
      ? 'Limit reached'
      : `Limit reached this ${e.limit?.per ?? 'week'}`;
  if (!canAfford(c, p.account, scaleCost(e.price, qty))) return 'Not enough currency';
  if (e.item && roomFor(c, e.item) < (e.count ?? 1) * qty)
    return (c.inventory.stacks[e.item] ?? 0) > 0 ? 'You can’t carry more' : 'Bag is full';
  if (e.gear && freeSlots(c) < qty) return 'Bag is full';
  return null;
}

export function buyFromShop(
  p: Progression,
  shop: ShopDef,
  entryId: string,
  qty = 1,
  rep = 0,
): ShopResult {
  const e = shop.entries(p.char).find((x) => x.id === entryId);
  if (!e) return { ok: false, reason: 'Sold out' };
  const block = blockReason(p, shop, e, qty, rep);
  if (block) return { ok: false, reason: block };
  if (!pay(p.char, p.account, scaleCost(e.price, qty)))
    return { ok: false, reason: 'Not enough currency' };
  recordPurchase(p.char, p.account, shop, e, qty);
  const items: { id: string; count: number }[] = [];
  const equip: { name: string; rarity: Rarity }[] = [];
  if (e.item) {
    const n = (e.count ?? 1) * qty;
    p.giveItem(e.item, n);
    items.push({ id: e.item, count: n });
  }
  if (e.gear) {
    for (let i = 0; i < qty; i++) {
      const g = makeGear(e.gear.slot, e.gear.ilvl, e.gear.rarity, {
        set: e.gear.set,
        quality: e.gear.quality ?? p.rng.int(30, 90),
      });
      p.char.inventory.items.push(g);
      p.emit('equipDrop', { item: g });
      equip.push({ name: g.name, rarity: g.rarity });
    }
  }
  p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  return { ok: true, items, equip };
}

// ---------------------------------------------------------------- selling

export interface SellRow {
  /** Stack item id or equipment uid. */
  key: string;
  kind: 'stack' | 'equip';
  id: string;
  name: string;
  rarity: Rarity;
  count: number;
  /** Silver per unit. */
  unit: number;
}

const UNSELLABLE = new Set(['quest', 'token']);

/** Everything in the bag a vendor will buy (equipped items are never listed). */
export function sellableRows(c: CharacterSave): SellRow[] {
  const rows: SellRow[] = [];
  for (const it of c.inventory.items)
    rows.push({
      key: it.uid,
      kind: 'equip',
      id: it.uid,
      name: it.name,
      rarity: it.rarity,
      count: 1,
      unit: itemValue(it),
    });
  for (const [id, n] of Object.entries(c.inventory.stacks)) {
    const def = ITEMS[id];
    if (!def || n <= 0 || def.value <= 0 || UNSELLABLE.has(def.kind)) continue;
    rows.push({
      key: id,
      kind: 'stack',
      id,
      name: def.name,
      rarity: def.rarity,
      count: n,
      unit: def.value,
    });
  }
  return rows;
}

/** Sells `n` of a row to a vendor; returns silver gained. */
export function sellRow(p: Progression, row: SellRow, n = row.count): number {
  const got =
    row.kind === 'equip'
      ? sellItem(p.char, p.account, row.id)
      : sellStack(p.char, p.account, row.id, Math.min(n, row.count));
  if (got > 0) p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  return got;
}
